import React from 'react'
import { Table } from 'react-bootstrap'
import ScreenshotPlaceholder from '../../../components/ScreenshotPlaceholder'
import { Tip, Warn, Code, ArticleLink, Ext, TemplateBtn } from '../_helpers'

export const SECTION_ARTICLES = [
  {
    id: 'welcome',
    category: 'getting-started',
    title: 'Bienvenue — comment fonctionne la plateforme',
    summary: 'Un tour d\'horizon rapide de ce que fait la plateforme et des étapes pour obtenir un résultat.',
    body: (
      <>
        <p>
          La plateforme EcoEmpower vous aide à planifier et gérer l\'énergie de votre communauté —
          en prévoyant la consommation d\'énergie des bâtiments, en optimisant les moments d\'utilisation,
          et en estimant les coûts et bénéfices d\'investissements tels que des panneaux solaires ou des batteries.
        </p>
        <p>L\'utilisation de la plateforme suit cinq étapes simples :</p>
        <ol>
          <li><strong>Créer une communauté</strong> — donnez-lui un nom, un pays et une devise.</li>
          <li><strong>Ajouter des bâtiments et des équipements</strong> — les bâtiments de votre communauté ainsi que
            tout équipement tel que des panneaux solaires, des batteries ou des bornes de recharge pour véhicules électriques.</li>
          <li><strong>Importer des données</strong> — des fichiers d\'énergie historiques (fichiers CSV) décrivant
            la consommation d\'énergie de chaque bâtiment, la production solaire/éolienne, et les prix de l\'électricité.</li>
          <li><strong>Lancer les outils</strong> — Prévisions, Optimisation et Planification à Long Terme.</li>
          <li><strong>Consulter les résultats</strong> — des graphiques et des chiffres que vous pouvez sauvegarder et exporter.</li>
        </ol>
        <Tip>
          Les outils fonctionnent mieux avec des données réelles, mais vous pouvez explorer la plateforme avant d\'en disposer —
          la plupart des outils basculent vers des exemples illustratifs afin que rien ne soit bloqué.
        </Tip>
        <ScreenshotPlaceholder
          img="dashboard-overview.png"
          description="The main Dashboard after logging in, showing the community count cards and the three action cards (Communities, Quick Start, Documentation)."
          source="Platform — log in and capture the main Dashboard (the page at the root URL, e.g. http://localhost/)."
          caption="Le Tableau de bord est votre point de départ."
        />
        <h5 className="mt-4">La place des données</h5>
        <p>
          La tâche la plus importante — et la plus courante — est d\'importer vos fichiers de données correctement.
          La section <strong>Préparer et importer les données</strong> de ce guide explique exactement
          comment procéder, avec des modèles prêts à l\'emploi. Si un import est rejeté,
          la section <strong>Dépannage</strong> explique chaque message d\'erreur en langage clair.
        </p>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'data-overview',
    category: 'data',
    title: 'De quels fichiers de données ai-je besoin ?',
    summary: 'La liste complète des fichiers de données, leur utilité et le moment où vous en avez besoin.',
    body: (
      <>
        <p>
          Les données sont importées sous forme de <strong>fichiers CSV</strong> — de simples fichiers tableur que vous pouvez
          créer dans Excel ou Google Sheets. Il existe deux groupes :
        </p>
        <ul>
          <li><strong>Données de bâtiment</strong> — importées pour un bâtiment spécifique (sa consommation d\'énergie, son PV en toiture, ses factures).</li>
          <li><strong>Données communautaires</strong> — importées une seule fois pour toute la communauté (un parc solaire partagé, une éolienne, les prix de l\'électricité, la météo).</li>
        </ul>

        <h5 className="mt-4">Fichiers de données de bâtiment</h5>
        <Table bordered size="sm" responsive className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Fichier</th><th>Colonnes (obligatoires)</th><th>À quoi il sert</th></tr>
          </thead>
          <tbody>
            <tr><td>Profil de charge du bâtiment</td><td><Code>timestamp</Code>, <Code>consumption_kwh</Code></td><td>La quantité d\'électricité consommée par le bâtiment chaque heure — nécessaire pour les prévisions et l\'optimisation.</td></tr>
            <tr><td>Production PV du bâtiment</td><td><Code>timestamp</Code>, <Code>generation_kwh</Code></td><td>La quantité d\'énergie produite chaque heure par le PV en toiture du bâtiment.</td></tr>
            <tr><td>Facture d\'énergie du bâtiment</td><td><Code>period_start</Code>, <Code>period_end</Code>, <Code>consumption_kwh</Code>, <Code>cost_eur</Code></td><td>Factures mensuelles — utilisées pour l\'analyse des coûts.</td></tr>
          </tbody>
        </Table>

        <h5 className="mt-4">Fichiers de données communautaires</h5>
        <Table bordered size="sm" responsive className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Fichier</th><th>Colonnes (obligatoires)</th><th>À quoi il sert</th></tr>
          </thead>
          <tbody>
            <tr><td>Profil de production PV</td><td><Code>timestamp</Code>, <Code>generation_kw</Code></td><td>Production d\'un parc solaire partagé par la communauté.</td></tr>
            <tr><td>Profil de production éolienne</td><td><Code>timestamp</Code>, <Code>generation_kw</Code></td><td>Production d\'une éolienne communautaire.</td></tr>
            <tr><td>Tarif d\'importation historique</td><td><Code>timestamp</Code>, <Code>import_price_eur_kwh</Code></td><td>Le prix payé pour acheter de l\'électricité, heure par heure, sur une année.</td></tr>
            <tr><td>Tarif d\'exportation historique</td><td><Code>timestamp</Code>, <Code>export_price_eur_kwh</Code></td><td>Le prix reçu pour revendre l\'électricité au réseau.</td></tr>
            <tr><td>Tarif d\'import/export Day-Ahead</td><td>idem ci-dessus</td><td>Les prix horaires du lendemain (24 lignes) — importés chaque jour pour l\'optimisation Day-Ahead.</td></tr>
            <tr><td>Météo solaire</td><td><Code>timestamp</Code>, <Code>ghi_w_m2</Code></td><td>Ensoleillement et température — améliore les prévisions solaires.</td></tr>
            <tr><td>Météo éolienne</td><td><Code>timestamp</Code>, <Code>wind_speed_m_s</Code></td><td>Vitesse du vent — améliore les prévisions éoliennes.</td></tr>
          </tbody>
        </Table>

        <h5 className="mt-4">D\'où proviennent ces données ?</h5>
        <p>En général, vous ne saisissez pas ces données manuellement — vous les exportez depuis un système que vous utilisez déjà :</p>
        <ul>
          <li><strong>Consommation du bâtiment</strong> — depuis le portail en ligne du compteur communicant du bâtiment,
            auprès de votre opérateur de réseau de distribution (DSO), ou depuis le compte client de votre fournisseur d\'électricité.</li>
          <li><strong>Production solaire / éolienne</strong> — depuis l\'application de monitoring ou le portail web de
            l\'onduleur ou de la turbine (généralement configuré par l\'installateur).</li>
          <li><strong>Tarifs</strong> — depuis votre contrat d\'électricité ou votre fournisseur ; les prix Day-Ahead
            proviennent de la bourse nationale de l\'électricité ou, là encore, de votre fournisseur.</li>
          <li><strong>Météo</strong> — depuis un service météorologique national ou un fournisseur de données météo gratuit. Ce fichier est facultatif.</li>
        </ul>
        <p>
          Ces systèmes permettent généralement d\'exporter une plage de dates choisie sous forme de fichier CSV ou Excel.
          Une fois ce fichier obtenu, les deux articles suivants expliquent comment le mettre en forme correctement.
        </p>

        <Tip>
          Vous n\'avez <strong>pas</strong> besoin de tous les fichiers pour commencer. L\'essentiel est un
          <strong> Profil de charge du bâtiment</strong> pour chaque bâtiment et un fichier de <strong>tarif</strong>.
          Ajoutez les fichiers de production et de météo ultérieurement pour améliorer la précision.
        </Tip>
        <p className="mt-3">
          Chaque type de fichier possède son propre article dans cette section, avec un exemple et un
          modèle téléchargeable. Commencez par <em>« Créer des fichiers CSV correctement »</em> ci-dessous —
          cela évite la grande majorité des problèmes d\'import.
        </p>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'data-csv-basics',
    category: 'data',
    title: 'Créer des fichiers CSV correctement (Excel et Google Sheets)',
    summary: 'Les règles d\'or pour un fichier valide — et l\'erreur numéro un à éviter.',
    body: (
      <>
        <p>
          Un <strong>fichier CSV</strong> est simplement un tableur enregistré dans un format texte simple.
          Vous le préparez dans Excel ou Google Sheets comme n\'importe quel tableur, puis vous l\'enregistrez au format CSV.
          Respectez ces règles et vos imports fonctionneront du premier coup.
        </p>

        <h5 className="mt-4">Les règles d\'or</h5>
        <ol>
          <li><strong>Première ligne = en-têtes de colonnes.</strong> La première ligne doit contenir les noms de colonnes
            exacts pour le type de fichier (par exemple <Code>timestamp</Code> et <Code>consumption_kwh</Code>).
            L\'orthographe et les minuscules doivent correspondre exactement.</li>
          <li><strong>Une ligne par heure.</strong> Chaque ligne suivante correspond à une heure de données. Une année
            complète représente <strong>8 760 lignes</strong> (365 jours × 24 heures), plus la ligne d\'en-tête.</li>
          <li><strong>Utilisez le format d\'horodatage</strong> <Code>2025-01-01T14:00:00Z</Code> — c\'est-à-dire
            Année-Mois-Jour, la lettre <Code>T</Code>, puis Heure:Minute:Seconde, puis la lettre <Code>Z</Code>.</li>
          <li><strong>Les nombres utilisent un point, jamais une virgule.</strong> Écrivez <Code>3.5</Code>, pas <Code>3,5</Code>.</li>
          <li><strong>Pas de lignes vides, pas de totaux, pas de notes supplémentaires</strong> — uniquement la ligne d\'en-tête et les lignes de données.</li>
        </ol>

        <Warn>
          <strong>L\'erreur la plus courante.</strong> En Italie, en France, en Allemagne, en Tchéquie et en Grèce,
          les tableurs écrivent normalement les nombres décimaux avec une <strong>virgule</strong>
          (<Code>0,457</Code>). La plateforme attend un <strong>point</strong> (<Code>0.457</Code>), donc
          un fichier avec des virgules est rejeté avec « La valeur doit être un nombre ». Si votre fichier contient déjà des virgules,
          vous n\'avez <strong>pas</strong> besoin de tout ressaisir — consultez{' '}
          <ArticleLink to="data-fix-commas">Corriger un fichier qui utilise des virgules comme séparateurs décimaux</ArticleLink>.
        </Warn>

        <h5 className="mt-4">À propos de la colonne d\'horodatage</h5>
        <p>
          Le format le plus fiable est <Code>2025-01-01T14:00:00Z</Code>, et il présente un avantage caché :
          Excel tend à reformater silencieusement tout ce qu\'il reconnaît comme une date, mais il laisse
          généralement cette forme intacte grâce au <Code>T</Code> et au <Code>Z</Code>. La
          plateforme comprend également les formats européens courants tels que <Code>01/01/2025 14:00</Code>
          et <Code>2025-01-01</Code> — mais ce sont précisément ceux qu\'Excel risque de modifier à votre insu.
        </p>
        <p>
          Si vos horodatages ont été altérés, ou si vous devez créer la colonne, utilisez cette formule dans une
          colonne libre (ici la date/heure d\'origine se trouve dans la cellule <Code>A2</Code>) :
        </p>
        <pre style={{ background: 'var(--color-neutral-50)', padding: 12, borderRadius: 6, fontSize: '0.8rem' }}>
{`=TEXT(A2,"yyyy-mm-ddThh:mm:ss")&"Z"`}
        </pre>
        <p>
          Copiez-la vers le bas sur toutes les lignes, puis sélectionnez la colonne et utilisez
          <strong> Copier → Collage spécial → Valeurs</strong> pour figer le résultat. Utilisez cette colonne comme
          valeur <Code>timestamp</Code>.
        </p>

        <h5 className="mt-4">Combien de lignes ? (résolution des données)</h5>
        <p>
          Une ligne par période. Les données <strong>horaires</strong> sont standard — une année complète représente
          <strong> 8 760 lignes</strong> — mais les données en <strong>15 minutes</strong> et <strong>journalières</strong>
          sont également acceptées. Un minimum de 24 lignes (une journée) est requis.
        </p>

        <h5 className="mt-4">Enregistrer au format CSV depuis Microsoft Excel</h5>
        <ol>
          <li>Cliquez sur <strong>Fichier → Enregistrer sous</strong>.</li>
          <li>Dans le menu déroulant du type de fichier, choisissez <strong>« CSV UTF-8 (délimité par des virgules) (*.csv) »</strong>.</li>
          <li>Cliquez sur <strong>Enregistrer</strong>. Si Excel avertit que certaines fonctionnalités seront perdues, cliquez sur <strong>Oui</strong> — c\'est normal.</li>
        </ol>
        <p className="small text-muted">
          Si votre version d\'Excel n\'affiche pas « CSV UTF-8 », choisissez plutôt « CSV (délimité par des virgules) ».
        </p>
        <ScreenshotPlaceholder
          img="excel-save-as-csv.png"
          description="Excel's Save As dialog with the file-type dropdown open, highlighting the 'CSV UTF-8 (Comma delimited)' option."
          source="Microsoft Excel — open any workbook → File → Save As → click the file-type dropdown. Capture the dropdown open with the CSV UTF-8 entry highlighted."
          caption="Dans Excel, choisissez toujours « CSV UTF-8 »."
        />

        <h5 className="mt-4">Enregistrer au format CSV depuis Google Sheets</h5>
        <ol>
          <li>Cliquez sur <strong>Fichier → Télécharger → Valeurs séparées par des virgules (.csv)</strong>.</li>
          <li>Le fichier est enregistré dans le dossier Téléchargements de votre ordinateur.</li>
        </ol>
        <ScreenshotPlaceholder
          img="gsheets-download-csv.png"
          description="Google Sheets File → Download submenu, highlighting 'Comma-separated values (.csv)'."
          source="Google Sheets (sheets.google.com) — open any spreadsheet → File → Download. Capture the submenu open with 'Comma-separated values (.csv)' highlighted."
          caption="Dans Google Sheets, utilisez Fichier → Télécharger → .csv."
        />

        <h5 className="mt-4">Vérifier votre fichier avant l\'import</h5>
        <p>
          Ouvrez le fichier <Code>.csv</Code> enregistré une dernière fois et confirmez les points ci-dessous. Pour être
          tout à fait sûr, ouvrez-le avec un éditeur de texte brut (<strong>Notepad</strong> sous Windows,
          <strong> TextEdit</strong> sur Mac) plutôt qu\'avec un tableur — un éditeur de texte affiche le
          contenu réel du fichier, tandis qu\'Excel réapplique votre mise en forme locale à l\'écran et peut masquer
          le vrai problème.
        </p>
        <p>Vérifiez que :</p>
        <ul>
          <li>La première ligne affiche les noms de colonnes corrects.</li>
          <li>Les nombres ressemblent à <Code>3.5</Code> (point), pas <Code>3,5</Code> (virgule).</li>
          <li>Les horodatages ressemblent à <Code>2025-01-01T14:00:00Z</Code>.</li>
          <li>Il n\'y a pas de lignes vides ni de lignes de synthèse en bas.</li>
        </ul>
        <Tip>
          Le plus simple : téléchargez un modèle depuis l\'un des articles spécifiques à chaque type de fichier dans cette
          section, remplacez les exemples de chiffres par les vôtres, et enregistrez. Le modèle contient déjà
          les en-têtes correctes et le bon format de nombre.
        </Tip>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'data-fix-commas',
    category: 'data',
    title: 'Corriger un fichier qui utilise des virgules comme séparateurs décimaux',
    summary: 'Convertir un fichier existant avec des nombres à virgule (3,5) vers le format point requis par la plateforme (3.5) — sans rien ressaisir.',
    body: (
      <>
        <p>
          Les tableurs en Italie, en France, en Allemagne, en Tchéquie et en Grèce écrivent normalement
          les nombres décimaux avec une <strong>virgule</strong> — <Code>0,457</Code> au lieu de
          {' '}<Code>0.457</Code>. La plateforme a besoin du format avec point. La bonne nouvelle : vous
          {' '}<strong>n\'avez pas</strong> besoin de tout ressaisir. Modifiez un paramètre, enregistrez le fichier
          à nouveau, et c\'est terminé. Utilisez la méthode qui correspond au logiciel que vous avez.
        </p>

        <h5 className="mt-3">Méthode 1 — Google Sheets (la plus simple, gratuite)</h5>
        <ol>
          <li>Rendez-vous sur <strong>sheets.google.com</strong> et ouvrez une feuille de calcul vierge.</li>
          <li>Choisissez <strong>Fichier → Importer → Téléverser</strong> et sélectionnez votre fichier CSV.</li>
          <li>Choisissez <strong>Fichier → Paramètres</strong>. Sous <strong>Paramètres régionaux</strong>, sélectionnez
            {' '}<strong>États-Unis</strong> (ou Royaume-Uni), puis <strong>Enregistrer les paramètres</strong>.
            Cela indique à Google Sheets d\'utiliser un point comme séparateur décimal.</li>
          <li>Choisissez <strong>Fichier → Télécharger → Valeurs séparées par des virgules (.csv)</strong>.</li>
        </ol>
        <p>Le fichier téléchargé utilise désormais des points et est prêt à être importé.</p>
        <ScreenshotPlaceholder
          img="gsheets-locale-setting.png"
          description="Google Sheets — the File → Settings dialog with the Locale dropdown open and 'United States' highlighted."
          source="Google Sheets (sheets.google.com) — open any spreadsheet → File → Settings → the General tab. Capture the modal with the Locale dropdown open and 'United States' highlighted."
          caption="Définir les paramètres régionaux sur États-Unis fait exporter des points à Google Sheets."
        />

        <h5 className="mt-4">Méthode 2 — Microsoft Excel</h5>
        <ol>
          <li>Ouvrez le fichier dans Excel, puis allez dans <strong>Fichier → Options → Options avancées</strong>.</li>
          <li>Sous <strong>Options d\'édition</strong>, décochez <strong>« Utiliser les séparateurs système »</strong>.</li>
          <li>Définissez le <strong>Séparateur décimal</strong> sur un point <Code>.</Code> et
            {' '}le <strong>Séparateur des milliers</strong> sur une espace simple (ou laissez-le vide).</li>
          <li>Cliquez sur <strong>OK</strong>, puis utilisez <strong>Fichier → Enregistrer sous</strong> et choisissez
            {' '}<strong>« CSV UTF-8 (délimité par des virgules) »</strong>.</li>
        </ol>
        <ScreenshotPlaceholder
          img="excel-separator-options.png"
          description="Excel — File → Options → Advanced, the 'Editing options' area, with 'Use system separators' unticked and the decimal/thousands separator boxes visible."
          source="Microsoft Excel — open any workbook → File → Options → Advanced tab → scroll to 'Editing options'. Capture the section with the 'Use system separators' checkbox unticked."
          caption="Dans Excel, désactivez les séparateurs système et définissez le séparateur décimal sur un point."
        />
        <Tip>Ce paramètre affecte tout Excel. Vous pouvez réactiver « Utiliser les séparateurs système » ensuite.</Tip>

        <h5 className="mt-4">Méthode 3 — Rechercher &amp; Remplacer (dernier recours)</h5>
        <p>
          N\'utilisez cette méthode que si les nombres sont stockés en texte brut et que le fichier ne contient <strong>aucun</strong>
          {' '}séparateur de milliers (rien du style <Code>1.234,5</Code>), car un remplacement aveugle
          endommagrait également ceux-ci.
        </p>
        <ol>
          <li>Sélectionnez uniquement les colonnes contenant des nombres.</li>
          <li>Ouvrez <strong>Rechercher &amp; Remplacer</strong> (Ctrl+H), cherchez <Code>,</Code>, remplacez par
            {' '}<Code>.</Code>, puis Remplacer tout.</li>
          <li>Vérifiez quelques valeurs avant d\'enregistrer.</li>
        </ol>

        <h5 className="mt-4">Qu\'en est-il des outils de conversion en ligne ?</h5>
        <p>
          Il existe des sites web gratuits de « conversion CSV », mais ils nécessitent d\'envoyer votre fichier à un
          site tiers, et les données énergétiques peuvent être sensibles. Nous recommandons plutôt les méthodes ci-dessus
          — elles utilisent des logiciels que vous avez déjà et conservent le fichier sur votre propre ordinateur. Si
          votre organisation utilise déjà Google Workspace, la méthode 1 reste dans des outils de confiance.
        </p>

        <Warn>
          Un fichier <Code>.csv</Code> ouvert dans Excel peut <em>sembler</em> contenir encore des virgules même
          lorsqu\'il a été enregistré avec des points, car Excel réapplique votre mise en forme locale à l\'écran. Pour
          voir ce qui se trouve <strong>réellement</strong> dans le fichier, ouvrez-le avec un éditeur de texte brut —
          {' '}<strong>Notepad</strong> sous Windows ou <strong>TextEdit</strong> sur Mac. Les
          nombres doivent y apparaître comme <Code>3.5</Code>.
        </Warn>
      </>
    ),
  },
]
